import  { useState, useEffect } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/system";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";

const UploadContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hasFile" && prop !== "disabled",
})(({ hasFile, disabled }) => ({
  width: "250px",
  height: "250px",
  border: `2px dashed ${disabled ? "#ccc" : hasFile ? "#2a9941" : "#d2d6da"}`,
  borderRadius: "15px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  cursor: disabled ? "not-allowed" : "pointer",
  position: "relative",
  overflow: "hidden",
  backgroundColor: disabled ? "#f0f0f0" : "transparent",
  transition: "all 0.3s ease-in-out",
  textAlign: "center",
  padding: "10px",
}));

const HiddenInput = styled("input")({
  display: "none",
});

const FileNameText = styled(Typography)({
  wordBreak: "break-word",
  maxWidth: "90%",
  textAlign: "center",
  marginTop: "10px",
});

const PDFSelector = ({ onFileSelect, fileUrl, disabled }) => {
  const [previewUrl, setPreviewUrl] = useState(fileUrl || null);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    setPreviewUrl(fileUrl);
  }, [fileUrl]);

  const handleFileChange = (event) => {
    if (disabled) return;
    const file = event.target.files[0];

    if (file) {
      const fileSizeLimit = 1 * 1024 * 1024;

      if (file.size > fileSizeLimit) {
        Swal.fire({
          icon: "error",
          title: "File too large!",
          text: "Please upload a PDF smaller than 1MB.",
          confirmButtonColor: "#d33",
        });
        return;
      }

      if (file.type === "application/pdf") {
        setSelectedFile(file);
        setPreviewUrl(file.name);
        onFileSelect(file);
      }
    }
  };

  const handleRemoveFile = (e) => {
    if (disabled) return;
    e.stopPropagation();
    setSelectedFile(null);
    setPreviewUrl(null);
    onFileSelect(null);
  };

  return (
    <Box>
      <UploadContainer
        onClick={() =>
          !disabled && document.getElementById("pdf-input").click()
        }
        disabled={disabled}
        hasFile={!!previewUrl}
      >
        {previewUrl ? (
          <>
            <PictureAsPdfIcon sx={{ fontSize: 60, color: "#d32f2f" }} />
            <FileNameText variant="body2">{previewUrl}</FileNameText>
            {!disabled && (
              <IconButton
                sx={{
                  position: "absolute",
                  top: 5,
                  right: 5,
                  background: "#2a9941",
                  color: "#fff",
                  "&:hover": { background: "#1ca444" },
                }}
                onClick={handleRemoveFile}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </>
        ) : (
          <>
            <PictureAsPdfIcon
              sx={{ fontSize: 50, color: disabled ? "#aaa" : "#2a9941" }}
            />
            <Typography
              variant="body2"
              sx={{ mt: 1, color: disabled ? "#aaa" : "#666" }}
            >
              {disabled ? "Disabled" : "Click to select a PDF"}
              <br />
              {!disabled && (
                <Typography
                  variant="caption"
                  sx={{ mt: 1, color: "#666", textAlign: "center" }}
                >
                  Max file size: 1MB
                </Typography>
              )}
            </Typography>
          </>
        )}
      </UploadContainer>

      <HiddenInput
        id="pdf-input"
        type="file"
        accept="application/pdf"
        onChange={handleFileChange}
        disabled={disabled}
      />
    </Box>
  );
};

export default PDFSelector;
